import type { SupportedLocale } from '@/lib/LocaleContext'

export interface LanguageOption {
  code: SupportedLocale
  flag: string
  /** Two letters for the compact header trigger. */
  short: string
  country: string
  language: string
}

/**
 * The locales the app ships, each in its own language.
 *
 * Endonyms on purpose — "Deutsch", not "German". Someone looking for their own
 * language scans for the word they use for it, and a list translated into the
 * current language is unreadable to exactly the person who needs to change it.
 *
 * Shared between the header selector and the account settings so the two lists
 * cannot drift. The API keeps 15 locales (short aliases like `pt` and `en`
 * included); these are the ones with a flag and a home on screen.
 */
const ALL_LANGUAGES: LanguageOption[] = [
  { code: 'en-US', flag: '🇺🇸', short: 'US', country: 'United States', language: 'English' },
  { code: 'pt-BR', flag: '🇧🇷', short: 'BR', country: 'Brasil', language: 'Português' },
  { code: 'es-AR', flag: '🇦🇷', short: 'AR', country: 'Argentina', language: 'Español' },
  { code: 'de-AT', flag: '🇦🇹', short: 'AT', country: 'Österreich', language: 'Deutsch' },
  { code: 'de-DE', flag: '🇩🇪', short: 'DE', country: 'Deutschland', language: 'Deutsch' },
  { code: 'es-ES', flag: '🇪🇸', short: 'ES', country: 'España', language: 'Español' },
  { code: 'it-IT', flag: '🇮🇹', short: 'IT', country: 'Italia', language: 'Italiano' },
  { code: 'es-MX', flag: '🇲🇽', short: 'MX', country: 'México', language: 'Español' },
  { code: 'pt-PT', flag: '🇵🇹', short: 'PT', country: 'Portugal', language: 'Português' },
  { code: 'de-CH', flag: '🇨🇭', short: 'CH', country: 'Schweiz', language: 'Deutsch' },
  { code: 'fr-FR', flag: '🇫🇷', short: 'FR', country: 'France', language: 'Français' },
  { code: 'fr-BE', flag: '🇧🇪', short: 'BE', country: 'Belgique', language: 'Français' },
  { code: 'fr-CA', flag: '🇨🇦', short: 'CA', country: 'Canada', language: 'Français' },
  { code: 'fr-CH', flag: '🇨🇭', short: 'CH', country: 'Suisse', language: 'Français' },
]

/**
 * Whoever is translating can see the pending locales in their own browser
 * with `localStorage.setItem('i18nPreviewPending', '1')` and a reload.
 */
const previewPending = (): boolean => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('i18nPreviewPending') === '1'
  } catch {
    return false
  }
}

/**
 * Locales whose translation is not complete yet. They stay off every picker
 * and out of browser detection, so nobody lands on a screen that falls back to
 * English halfway through. Remove a code once its last namespace is in.
 */
const PENDING: readonly string[] = []

export const PENDING_LOCALES: readonly string[] = previewPending() ? [] : PENDING

export const LANGUAGES: LanguageOption[] = ALL_LANGUAGES.filter((lang) => !PENDING_LOCALES.includes(lang.code))

export const findLanguage = (code?: string | null): LanguageOption | undefined =>
  LANGUAGES.find((lang) => lang.code === code)
